// HLS (.m3u8) playback support.
// Prefer hls.js whenever Media Source Extensions are available. Newer Chrome
// versions report "maybe" for native HLS even though native playback is not
// reliable, so canPlayType() must only be used as the fallback path.

const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.7.3/dist/hls.min.js'
const ATTACHED_FLAG = 'layHlsAttached'
const HLS_INSTANCE_PROPERTY = '_layHlsInstance'

let hlsScriptPromise = null
let errorFallbackBound = false
let cleanupObserver = null
const attachedHlsVideos = new Set()

const isHlsUrl = (url) => {
	if (!url || typeof url !== 'string') return false
	const clean = url.split('?')[0].split('#')[0]
	return /\.m3u8$/i.test(clean)
}

// Only returns a URL when it's actually wired up (src=...), not when
// it's only sitting in data-src waiting for the lazyloader. Lazy code
// paths call attachHlsIfNeeded() themselves once they promote data-src
// to src, so honoring data-src here would defeat lazyloading.
const getHlsUrlFromVideo = (video) => {
	if (!video) return ''
	const directSrc = video.getAttribute && video.getAttribute('src')
	if (isHlsUrl(directSrc)) return directSrc
	const children = video.children || []
	for (let i = 0; i < children.length; i++) {
		const child = children[i]
		if (!child || child.tagName !== 'SOURCE') continue
		const src = child.getAttribute('src')
		if (isHlsUrl(src)) return src
	}
	return ''
}

const loadHlsLib = () => {
	if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
	if (window.Hls) return Promise.resolve(window.Hls)
	if (hlsScriptPromise) return hlsScriptPromise
	hlsScriptPromise = new Promise((resolve, reject) => {
		const script = document.createElement('script')
		script.src = HLS_CDN
		script.async = true
		script.crossOrigin = 'anonymous'
		script.onload = () => {
			if (window.Hls) {
				resolve(window.Hls)
			} else {
				hlsScriptPromise = null
				reject(new Error('hls.js loaded but window.Hls undefined'))
			}
		}
		script.onerror = (err) => {
			hlsScriptPromise = null
			reject(err)
		}
		document.head.appendChild(script)
	})
	return hlsScriptPromise
}

// Safari (desktop + iOS) reports support for application/vnd.apple.mpegurl.
const nativeHlsSupported = (video) => {
	if (!video || typeof video.canPlayType !== 'function') return false
	return Boolean(video.canPlayType('application/vnd.apple.mpegurl') || video.canPlayType('application/x-mpegURL'))
}

const useNativeHlsFallback = (video) => {
	if (!nativeHlsSupported(video)) return false
	video.dataset[ATTACHED_FLAG] = 'native'
	return true
}

const destroyHlsForVideo = (video) => {
	if (!video) return
	const hls = video[HLS_INSTANCE_PROPERTY]
	if (hls && typeof hls.destroy === 'function') {
		try {
			hls.destroy()
		} catch (e) {}
	}
	try {
		delete video[HLS_INSTANCE_PROPERTY]
	} catch (e) {
		video[HLS_INSTANCE_PROPERTY] = null
	}
	if (video.dataset) delete video.dataset[ATTACHED_FLAG]
	attachedHlsVideos.delete(video)
}

const destroyAllHls = () => {
	Array.from(attachedHlsVideos).forEach(destroyHlsForVideo)
}

const attachHlsIfNeeded = (video) => {
	if (!video || video.tagName !== 'VIDEO') return
	if (video.dataset[ATTACHED_FLAG]) return
	const url = getHlsUrlFromVideo(video)
	if (!url) return
	video.dataset[ATTACHED_FLAG] = 'pending'

	loadHlsLib()
		.then((Hls) => {
			if (!video.isConnected) {
				delete video.dataset[ATTACHED_FLAG]
				return
			}
			if (!Hls || typeof Hls.isSupported !== 'function' || !Hls.isSupported()) {
				if (!useNativeHlsFallback(video)) video.dataset[ATTACHED_FLAG] = 'unsupported'
				return
			}
			// Detach <source> children so the browser stops trying to fetch
			// the .m3u8 itself; hls.js will set its own src on the video.
			const sources = Array.from(video.querySelectorAll('source'))
			sources.forEach((s) => {
				if (s.parentNode) s.parentNode.removeChild(s)
			})
			try {
				video.removeAttribute('src')
			} catch (e) {}
			try {
				video.load()
			} catch (e) {}

			const hls = new Hls()
			video[HLS_INSTANCE_PROPERTY] = hls
			attachedHlsVideos.add(video)
			hls.loadSource(url)
			hls.attachMedia(video)
			video.dataset[ATTACHED_FLAG] = '1'

			// Keep autoplay behavior consistent with the rest of the theme:
			// once manifest is parsed and the video can play, retry play()
			// if the element wants to autoplay.
			hls.on(Hls.Events.MANIFEST_PARSED, () => {
				if (video.autoplay || video.classList.contains('autoplay')) {
					const p = video.play()
					if (p && typeof p.catch === 'function') p.catch(() => {})
				}
			})
		})
		.catch(() => {
			destroyHlsForVideo(video)
			if (!useNativeHlsFallback(video)) video.dataset[ATTACHED_FLAG] = 'failed'
		})
}

const scanAndAttachAll = (root) => {
	if (!root) return
	const node = root && root.jquery ? root[0] : root
	if (!node || typeof node.querySelectorAll !== 'function') return
	node.querySelectorAll('video').forEach(attachHlsIfNeeded)
}

// Safety net: if any video on the page tries to load an .m3u8 and the
// browser errors out (because it's not Safari and we didn't proactively
// attach hls.js), catch the error in capture phase and attach.
const bindErrorFallback = () => {
	if (errorFallbackBound) return
	errorFallbackBound = true
	document.addEventListener(
		'error',
		(e) => {
			const t = e.target
			if (!t) return
			if (t.tagName === 'SOURCE') {
				const video = t.parentElement
				if (video && video.tagName === 'VIDEO') attachHlsIfNeeded(video)
			} else if (t.tagName === 'VIDEO') {
				attachHlsIfNeeded(t)
			}
		},
		true
	)
}

const bindCleanup = () => {
	if (cleanupObserver || typeof MutationObserver === 'undefined') return
	cleanupObserver = new MutationObserver((records) => {
		records.forEach((record) => {
			Array.from(record.removedNodes || []).forEach((node) => {
				if (!node || node.nodeType !== 1) return
				if (node.tagName === 'VIDEO' && !node.isConnected) destroyHlsForVideo(node)
				if (typeof node.querySelectorAll === 'function') {
					node.querySelectorAll('video').forEach((video) => {
						if (!video.isConnected) destroyHlsForVideo(video)
					})
				}
			})
		})
	})
	cleanupObserver.observe(document.documentElement, { childList: true, subtree: true })
	window.addEventListener('pagehide', destroyAllHls)
}

const init = () => {
	scanAndAttachAll(document)
	bindErrorFallback()
	bindCleanup()
	if (window.laytheme && typeof window.laytheme.on === 'function') {
		// Scope to event scope so a future double-fire (overlay + lazy underlay)
		// only walks its own .lay-content per fire, not the whole document twice.
		// attachHlsIfNeeded is idempotent at the element level (skips already-
		// attached <video>s) so the worst case today is wasted DOM walk.
		window.laytheme.on('newpageshown', (layoutObj, type, obj, isProjectOverlayObj) => {
			const isProjectOverlay = isProjectOverlayObj && isProjectOverlayObj.isProjectOverlay === true
			const root = isProjectOverlay
				? document.querySelector('#lay-project-overlay > .lay-content')
				: document.querySelector('body > .lay-content')
			scanAndAttachAll(root || document)
		})
	}
}

const hls_loader = {
	init,
	attachHlsIfNeeded,
	scanAndAttachAll,
	isHlsUrl,
	destroyHlsForVideo,
	destroyAllHls,
}

if (typeof window !== 'undefined') {
	window.layHls = hls_loader
}

export default hls_loader
export { attachHlsIfNeeded, scanAndAttachAll, isHlsUrl, destroyHlsForVideo, destroyAllHls }
